import { useState } from 'react';

import { useTheme } from '@emotion/react';
import { ChevronRight, Search } from '@mui/icons-material';
import { Box, CircularProgress, IconButton, Modal, Typography } from '@mui/material';
import { Address } from 'types/graphql';

import CloseIcon from 'src/assets/images/icons/CloseButtonSVG';
import Loader from 'src/components/Loader/Loader';
import { useAddressLookUp } from 'src/hooks';

type AddressData = {
    id: string;
    text: string;
};

interface AddressListModalProps {
    isOpen: boolean;
    addressList: AddressData[];
    onClose: () => void;
    onSelectedAddress: (addressData: Address, text: string) => void;
}

const AddressListModal = ({
    isOpen,
    addressList = [],
    onClose,
    onSelectedAddress,
}: AddressListModalProps) => {
    const theme = useTheme();
    const [selectedAddressId, setSelectedAddressId] = useState<string>();
    const { getAddressDetails, gettingAddressDetails } = useAddressLookUp();

    const onGetAddressDetails = async (id: string, formattedAddress: string) => {
        setSelectedAddressId(id);
        await getAddressDetails(id, null, (addresses) => {
            onSelectedAddress({ ...addresses[0] }, formattedAddress);
            onClose && onClose();
        });
    };

    return (
        <Modal open={isOpen} onClose={onClose}>
            <Box
                maxWidth="90%"
                margin="20px auto"
                height="90vh"
                sx={{ overflowY: 'scroll', borderRadius: '16px' }}
            >
                <>
                    {gettingAddressDetails && <Loader />}
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{
                            borderTopLeftRadius: '16px',
                            borderTopRightRadius: '16px',
                            background: `linear-gradient(90deg, ${theme.palette.orange.main2} 0%, ${theme.palette.orange.light2} 100%)`,
                            padding: '24px',
                        }}
                    >
                        <Box
                            display="flex"
                            alignItems="center"
                            color={`${theme.palette.orange.light2}`}
                        >
                            <Search color="inherit" />
                            <Typography
                                fontSize="24px"
                                color={`${theme.palette.orange.light2}`}
                                marginLeft="14px"
                            >
                                Search
                            </Typography>
                        </Box>
                        <IconButton onClick={onClose}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                    <Box
                        bgcolor="white"
                        sx={{
                            borderBottomLeftRadius: '16px',
                            borderBottomRightRadius: '16px',
                        }}
                    >
                        <Box padding="24px" borderBottom={`1px solid ${theme.palette.grey[500]}`}>
                            Search and filters
                        </Box>
                        <Box padding="24px">
                            <Typography
                                color={theme.palette.text[300]}
                                fontSize="20px"
                                lineHeight="40px"
                            >
                                {addressList?.length ? 'Results found' : '0 Result Found'}
                            </Typography>
                            <Typography
                                color={theme.palette.text[1000]}
                                fontSize="10px"
                                lineHeight="28px"
                            >
                                The following results were returned
                            </Typography>
                            <Box
                                height="2px"
                                width="100%"
                                bgcolor={theme.palette.orange.main}
                                marginBottom="24px"
                            />
                            <Box
                                display="flex"
                                flexDirection="column"
                                flex={1}
                                gap="16px"
                                width="100%"
                            >
                                {addressList.map((address) => {
                                    return (
                                        <Box
                                            key={address.id}
                                            borderRadius="16px"
                                            overflow="hidden"
                                            display="flex"
                                            width="100%"
                                        >
                                            <Box
                                                width="100%"
                                                padding="24px"
                                                border={`1px solid ${theme.palette.grey[500]}`}
                                                borderRight="0px"
                                                sx={{
                                                    borderTopLeftRadius: '16px',
                                                    borderBottomLeftRadius: '16px',
                                                }}
                                            >
                                                <Typography
                                                    color={theme.palette.text[400]}
                                                    fontSize="20px"
                                                    lineHeight="28.6px"
                                                >
                                                    {address.text}
                                                </Typography>
                                            </Box>
                                            <Box
                                                bgcolor={theme.palette.orange.main}
                                                display="flex"
                                                justifyContent="center"
                                                alignItems="center"
                                                padding="20px"
                                                onClick={async () => {
                                                    if (!gettingAddressDetails) {
                                                        await onGetAddressDetails(
                                                            address.id,
                                                            address.text
                                                        );
                                                    }
                                                }}
                                                sx={{ cursor: 'pointer' }}
                                            >
                                                {gettingAddressDetails &&
                                                selectedAddressId === address?.id ? (
                                                    <CircularProgress
                                                        size={34}
                                                        sx={{
                                                            color: `${theme.palette.common.white}`,
                                                        }}
                                                    />
                                                ) : (
                                                    <ChevronRight
                                                        sx={{
                                                            color: `${theme.palette.common.white}`,
                                                        }}
                                                        fontSize="large"
                                                    />
                                                )}
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Box>
                    </Box>
                </>
            </Box>
        </Modal>
    );
};
export default AddressListModal;
